/**
 * Avisos de conexión del portal médico (propuesta «Avisos de conexión en la teleconsulta»).
 *
 * Vive DENTRO de `<LiveKitRoom>` porque el detector necesita el contexto de la sala. Solo pinta
 * el peldaño discreto (8–25 s); el peldaño con instrucción es el `ReconnectionOverlay`, al que
 * este componente le pasa el nivel a través de `onNivelChange` para que sepa cuándo abrirse.
 */
import { useEffect } from 'react';
import { TriangleAlert, AudioLines } from 'lucide-react';
import { useAvisoConexion } from '../../hooks/useAvisoConexion';
import { TEXTO_LEVE, textoSoloAudio, type NivelAviso } from '../../hooks/avisoConexion';
import styles from './AvisoConexionBanner.module.css';

export interface AvisosConexionProps {
  consultaId?: string;
  /** La cámara o el micrófono propios se soltaron (lo reporta `MediaSync`). */
  medioPropioCaido?: boolean;
  /** El nivel se levanta al padre para que el overlay decida cuándo interrumpir. */
  onNivelChange?: (nivel: NivelAviso) => void;
  /** Reintentar SOLO el video, sin tocar la conexión: el audio que funciona no se interrumpe. */
  onReintentarVideo?: () => void;
}

export function AvisosConexion({
  consultaId,
  medioPropioCaido,
  onNivelChange,
  onReintentarVideo,
}: AvisosConexionProps) {
  const aviso = useAvisoConexion({ consultaId, medioPropioCaido });

  useEffect(() => {
    onNivelChange?.(aviso.nivel);
  }, [aviso.nivel, onNivelChange]);

  // Al remontarse `<LiveKitRoom>` (reconexión forzada) este componente se destruye y el
  // detector arranca de cero; sin esto el padre se quedaría con el último nivel grave y el
  // overlay seguiría abierto sobre una llamada ya sana.
  useEffect(() => () => onNivelChange?.('ninguno'), [onNivelChange]);

  /* Modo solo audio: el médico dejó de ver a su paciente y necesita saber por qué —si no, lo
     lee como que se fue o como una avería—. No abre overlay ni interrumpe: la consulta sigue,
     y el ofrecimiento es reintentar el VIDEO, no reconectar la llamada. */
  if (aviso.nivel === 'soloAudio') {
    const texto = textoSoloAudio(true);
    return (
      <div className={`${styles.banner} ${styles.bannerInfo}`} role="status" aria-live="polite">
        <AudioLines size={16} className={styles.icono} aria-hidden="true" />
        <span>
          <span className={styles.titulo}>{texto.titulo}</span>
          {' · '}
          <span className={styles.detalle}>{texto.detalle}</span>
        </span>
        {onReintentarVideo && (
          <button type="button" className={styles.accion} onClick={onReintentarVideo}>
            {texto.accion}
          </button>
        )}
      </div>
    );
  }

  if (aviso.nivel !== 'leve') return null;

  return (
    /* `polite`, no `assertive`: en este peldaño todavía no hay que hacer nada, y un lector de
       pantalla interrumpiendo al médico en plena consulta sería peor que el propio problema. */
    <div className={styles.banner} role="status" aria-live="polite">
      <TriangleAlert size={16} className={styles.icono} aria-hidden="true" />
      <span>
        <span className={styles.titulo}>{TEXTO_LEVE.titulo}</span>
        {' · '}
        <span className={styles.detalle}>{TEXTO_LEVE.detalle}</span>
      </span>
    </div>
  );
}
